import { defineMessages, useIntl } from 'react-intl';

import { useContentfulContentTypeEntry } from '@order/Contentful';

// ─────────────────────────────────────────────────────────────────────────────

export const useCartHandoffContent = (params: CartHandoffContentParams) => {
  const {
    isHandingOffCart,
    hasHandoffCompleted,
    isReorderUnavailable,
    hasConflicts,
    hasActiveBag,
    reorderConflicts,
  } = params;

  const { formatMessage, formatList } = useIntl();
  const contentfulData = useContentfulContentTypeEntry<ContentfulEntry>({ contentType: 'cartHandoff' }); // prettier-ignore
  const content = contentfulData.data?.fields;

  if (hasHandoffCompleted && !isHandingOffCart) {
    return {
      title: content?.completeTitle ?? formatMessage(messages.completeTitle),
      body: undefined,
      cta: formatMessage(messages.close),
    };
  }

  if (isReorderUnavailable && !isHandingOffCart) {
    return {
      title: content?.unavailableTitle ?? formatMessage(messages.unavailableTitle),
      body: hasActiveBag
        ? (content?.unavailableBodyWithBag ?? formatMessage(messages.unavailableBodyWithBag))
        : (content?.unavailableBodyWithoutBag ?? formatMessage(messages.unavailableBodyWithoutBag)),
      cta: content?.unavailableCta ?? formatMessage(messages.unavailableCta),
    };
  }

  if (hasConflicts && !isHandingOffCart) {
    const conflict = reorderConflicts[0];
    const conflicts = formatList(reorderConflicts);

    return {
      title: content?.conflictsTitle ?? formatMessage(messages.conflictsTitle),
      body:
        reorderConflicts?.length === 1
          ? (content?.conflictsBodySingle.replaceAll('{conflict}', conflict) ??
            formatMessage(messages.conflictsBodySingle, { conflict }))
          : (content?.conflictsBodyMultiple.replaceAll('{conflicts}', conflicts) ??
            formatMessage(messages.conflictsBodyMultiple, { conflicts })),
      cta: content?.conflictsCta ?? formatMessage(messages.conflictsCta),
    };
  }

  if (hasActiveBag && !isHandingOffCart) {
    return {
      title: content?.activeBagTitle ?? formatMessage(messages.activeBagTitle),
      body: content?.activeBagBody ?? formatMessage(messages.activeBagBody),
      cta: content?.activeBagCta ?? formatMessage(messages.activeBagCta),
    };
  }

  return {
    title: content?.defaultTitle ?? formatMessage(messages.defaultTitle),
    body: undefined,
    cta: formatMessage(messages.close),
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  defaultTitle: {
    defaultMessage: 'Setting up your order...',
    description: 'Cart Handoff | Default | Title',
  },
  completeTitle: {
    defaultMessage: 'Continuing to checkout...',
    description: 'Cart Handoff | Complete | Title',
  },
  activeBagTitle: {
    defaultMessage: 'You have an active bag',
    description: 'Cart Handoff | Active Bag | Title',
  },
  activeBagBody: {
    defaultMessage: 'Proceeding with a pre-made order will clear your current bag.',
    description: 'Cart Handoff | Active Bag | Body',
  },
  activeBagCta: {
    defaultMessage: 'Clear bag & continue',
    description: 'Cart Handoff | Active Bag | CTA',
  },
  conflictsTitle: {
    defaultMessage: 'Partial availability',
    description: 'Cart Handoff | Conflicts | Body',
  },
  conflictsBodySingle: {
    defaultMessage: '{conflict} is not available and will not be added to your bag.',
    description: 'Cart Handoff | One missing conflict',
  },
  conflictsBodyMultiple: {
    defaultMessage: '{conflicts} are not available and will not be added to your bag.',
    description: 'Cart Handoff | Many missing conflicts',
  },
  conflictsCta: {
    defaultMessage: 'Continue',
    description: 'Cart Handoff | Conflicts | CTA',
  },
  unavailableTitle: {
    defaultMessage: 'Something went wrong',
    description: 'Cart Handoff | Unavailable | Title',
  },
  unavailableBodyWithBag: {
    defaultMessage: "We couldn't recreate your order. Your current bag has not been cleared.",
    description: 'Cart Handoff | Unavailable | Body',
  },
  unavailableBodyWithoutBag: {
    defaultMessage: `We couldn't recreate your order. Please try again or head to the menu and build a new order.`,
    description: 'Cart Handoff | Unavailable | Body',
  },
  unavailableCta: {
    defaultMessage: 'Close',
    description: 'Cart Handoff | Close',
  },
  close: {
    defaultMessage: 'Close',
    description: 'Cart Handoff | Close',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CartHandoffContentParams = {
  isHandingOffCart: boolean;
  hasHandoffCompleted: boolean;
  isReorderUnavailable: boolean;
  hasConflicts: boolean;
  hasActiveBag: boolean;
  reorderConflicts: string[];
};

type ContentfulEntry = {
  defaultTitle: string;
  completeTitle: string;
  activeBagTitle: string;
  activeBagBody: string;
  activeBagCta: string;
  conflictsTitle: string;
  conflictsBodySingle: string;
  conflictsBodyMultiple: string;
  conflictsCta: string;
  unavailableTitle: string;
  unavailableBodyWithBag: string;
  unavailableBodyWithoutBag: string;
  unavailableCta: string;
};
